import { useState } from "react";
import {
    Box,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    Typography,
    SelectChangeEvent,
    Paper,
    Divider
} from "@mui/material";
import { usePostBodyWithJwtMutation } from "src/redux/api/cabinet";

type CurrencyType = "usdtbsc" | "btc";

interface AlertState {
    type: number;
    msg: string;
}

interface PaymentData {
    operation_id: number;
    payment_url: string;
    pay_address: string | null;
    pay_amount: number;
    pay_currency: string;
    order_id: string;
    payment_id: string;
    status: string;
    amount_rub: number;
    network: string;
}

interface PaymentResponse {
    status: boolean;
    message: string;
    totalRub: string;
    totalCrypto: string;
    exchangeRate: number;
    payLink: string;
    typePay: string;
    data: PaymentData;
}

export const FormCrypto = () => {
    const [amount, setAmount] = useState<string>("");
    const [currency, setCurrency] = useState<CurrencyType>("usdtbsc");
    const [paymentData, setPaymentData] = useState<PaymentResponse | null>(null);
    const [alert, setAlert] = useState<AlertState | null>(null);

    const [getData, { isLoading }] = usePostBodyWithJwtMutation();

    const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        // Разрешаем пустую строку
        if (value === "") {
            setAmount(value);
            return;
        }

        // Проверяем паттерн: цифры, опционально точка и до 10 цифр после точки
        // Не разрешаем отрицательные значения (нет знака минус)
        const regex = /^\d+(\.\d{0,10})?$/;

        if (regex.test(value)) {
            setAmount(value);
        }
    };

    const handleCurrencyChange = (e: SelectChangeEvent<CurrencyType>) => {
        setCurrency(e.target.value as CurrencyType);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const amountNum = parseFloat(amount);

        // if (!amount || amountNum < 10) {
        //     setAlert({ type: 2, msg: "Минимальная сумма пополнения - 1 u" });
        //     return;
        // }

        setAlert(null);
        setPaymentData(null);

        const jwt = localStorage.getItem("jwt");

        try {
            const payload: PaymentResponse = await getData({
                body: {
                    currency,
                    amount: amountNum
                },
                route: "payeer",
                jwt
            }).unwrap();

            if (payload?.status && payload?.data) {
                setPaymentData(payload);
                setAlert({ type: 1, msg: payload.message });
            }
        } catch (error: any) {
            setAlert({
                type: 2,
                msg: error?.data?.message || "Произошла ошибка при создании ссылки"
            });
        }
    };

    const handlePayment = () => {
        if (paymentData?.payLink) {
            window.open(paymentData.payLink, "_blank", "noopener,noreferrer");
        }
    };

    const handleBack = () => {
        setPaymentData(null);
        setAlert(null);
    };

    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
                maxWidth: 500,
                mx: "auto",
                p: 3,
                display: "flex",
                flexDirection: "column",
                gap: 3
            }}
        >
            <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
                Пополнение криптовалютой
            </Typography>

            {!paymentData ? (
                <>
                    <TextField
                        label="Сумма в криптовалюте"
                        value={amount}
                        onChange={handleAmountChange}
                        required
                        fullWidth
                        // helperText="Минимальная сумма: 10 рублей"
                        type="text"
                        inputMode="decimal"
                    />

                    <FormControl fullWidth required>
                        <InputLabel>Криптовалюта</InputLabel>
                        <Select
                            sx={{
                                "&>.MuiOutlinedInput-notchedOutline": {
                                    background: 'transparent',
                                    border: `1px solid #bebebe`
                                }
                            }}
                            value={currency}
                            label="Криптовалюта"
                            onChange={handleCurrencyChange}
                        >
                            <MenuItem value="usdtbsc">USDT BEP20</MenuItem>
                            <MenuItem value="btc">BTC</MenuItem>
                        </Select>
                    </FormControl>

                    {alert && (
                        <Alert severity={alert.type === 1 ? "success" : "error"}>
                            {alert.msg}
                        </Alert>
                    )}

                    <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={isLoading || !amount || parseFloat(amount) <= 0}
                        fullWidth
                    >
                        {isLoading ? "Создание ссылки..." : "Получить ссылку для оплаты"}
                    </Button>
                </>
            ) : (
                <>
                    {alert && (
                        <Alert severity={alert.type === 1 ? "success" : "error"}>
                            {alert.msg}
                        </Alert>
                    )}

                    <Paper
                        elevation={2}
                        sx={{
                            p: 3,
                            bgcolor: "success.light",
                            borderRadius: 2
                        }}
                    >
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                            Детали платежа
                        </Typography>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                <Typography variant="body2" color="text.secondary">
                                    Сумма пополнения:
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                    {paymentData.totalRub}
                                </Typography>
                            </Box>

                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                <Typography variant="body2" color="text.secondary">
                                    Сумма пополнения с комиссией:
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                    {paymentData.totalCrypto}
                                </Typography>
                            </Box>

                            {/* <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                <Typography variant="body2" color="text.secondary">
                                    Курс обмена:
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                    {paymentData.exchangeRate} USDT
                                </Typography>
                            </Box> */}

                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                <Typography variant="body2" color="text.secondary">
                                    Сеть:
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                    {paymentData.data.network}
                                </Typography>
                            </Box>

                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                <Typography variant="body2" color="text.secondary">
                                    ID операции:
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                    {paymentData.data.operation_id}
                                </Typography>
                            </Box>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ display: "flex", gap: 2 }}>
                            <Button
                                variant="outlined"
                                color="primary"
                                size="large"
                                fullWidth
                                onClick={handleBack}
                            >
                                Назад
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                fullWidth
                                onClick={handlePayment}
                            >
                                Перейти к оплате
                            </Button>
                        </Box>
                    </Paper>
                </>
            )}
        </Box>
    );
};